<template>
  <main-buttons-bar :align="'space-between'">
    <template #left>
      <custom-title>Listado de productos personalizados</custom-title>
    </template>
    <template #right>
      <el-button
        type="primary"
        size="default"
        :icon="IconAdd"
        style="margin-right: 1rem"
        @click="nuevoElementoTipo"
        >Nuevo producto personalizado</el-button
      >
      <setting-modal-wrapper
        v-if="modalNuevoProductoVisible"
        :visible="modalNuevoProductoVisible"
        :title="tituloModal"
        @cerrar-modal="cerrarModalNuevoProducto"
      >
        <setting-create-product
          v-if="componente === 'SettingCreateProduct'"
          @close="cerrarModalNuevoProducto"
          @cancel="cerrarModalNuevoProducto"
          @created="onProductoCreado"
        />
        <setting-detail-product
          v-if="componente === 'SettingDetailProduct'"
          v-model="elementoSeleccionado"
          @close="cerrarModalNuevoProducto"
          @edit="editarDesdeDetalle"
        />
        <setting-edit-product
          v-if="componente === 'SettingEditProduct'"
          :codigo="elementoSeleccionado.Codigo"
          :configuracion-completa="configuracionCompleta"
          @close="cerrarModalNuevoProducto"
          @guardar="handleGuardarProducto"
          @created="onProductoCreado"
        />
      </setting-modal-wrapper>
    </template>
  </main-buttons-bar>
  <el-table
    ref="custom_table"
    :data="productTypeList"
    :style="{ width: '100%' }"
    :highlight-current-row="false"
    empty-text="Sin datos"
    border
  >
    <el-table-column
      prop="Codigo"
      label="Código"
      width="120"
      sortable
    ></el-table-column>
    <el-table-column
      prop="Descripcion"
      label="Descripción"
      width="200"
      sortable
    ></el-table-column>
    <el-table-column
      prop="ElementoBase.Descripcion"
      label="Elemento tipo"
      width="180"
      sortable
    ></el-table-column>
    <el-table-column
      label="Activo"
      width="80"
      align="center"
    >
      <template #default="scope">
        <el-tag
          :type="scope.row.Activo ? 'success' : 'danger'"
          size="small"
        >
          {{ scope.row.Activo ? 'Sí' : 'No' }}
        </el-tag>
      </template>
    </el-table-column>
    <el-table-column
      label="Tintas"
      width="80"
      align="center"
    >
      <template #default="scope">
        <el-tag
          :type="scope.row.Tintas ? 'success' : 'info'"
          size="small"
        >
          {{ scope.row.Tintas ? 'Sí' : 'No' }}
        </el-tag>
      </template>
    </el-table-column>
    <el-table-column
      label="Acciones"
      width="200"
      align="center"
    >
      <template #default="scope">
        <!-- <el-button :icon="IconVisibility" @click.prevent="verElementoTipo(scope.row)" /> -->
        <el-button
          :icon="IconEdit"
          :loading="isLoadingEdit(scope.row.Codigo)"
          :disabled="isLoadingDuplicate(scope.row.Codigo)"
          @click.prevent="modificarElementoTipo(scope.row)"
        />
        <el-button
          :icon="IconContentCopy"
          :loading="isLoadingDuplicate(scope.row.Codigo)"
          :disabled="isLoadingEdit(scope.row.Codigo)"
          @click.prevent="duplicarElementoTipo(scope.row)"
        />
        <!-- <el-button :icon="IconDelete" @click.prevent="deshabilitarElementoTipo(scope.row)" /> -->
      </template>
    </el-table-column>
  </el-table>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
  computed,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessage } from 'element-plus';
import IconAdd from '~icons/mdi/add';
import IconDelete from '~icons/mdi/delete';
import IconEdit from '~icons/mdi/edit';
import IconContentCopy from '~icons/mdi/content-copy';
import IconVisibility from '~icons/mdi/visibility';
import elementoTipoService from '@/services/elemento_tipo_service.js';
import CustomTitle from '@/shared/components/custom_title.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import SettingModalWrapper from './custom_product/setting_modal_wrapper.vue';
import SettingCreateProduct from './custom_product/setting_create_product.vue';
import SettingEditProduct from './custom_product/setting_edit_product.vue';
import SettingDetailProduct from './custom_product/setting_detail_product.vue';
// import SettingEditProductV2 from './custom_product/setting_edit_product_v2.vue'
import { useElementoTipoStore } from '@/stores/elemento_tipo/elemento_tipo_store.js';
import { useElementoGrupoStore } from '../../../stores/elemento_grupo/elemento_grupo_store';

const modalNuevoProductoVisible = ref(false);
const componente = ref('');
const isLoading = ref(false);
const loadingEdit = ref(new Set()); // Para controlar loading por elemento específico
const loadingDuplicate = ref(new Set()); // Para controlar loading de duplicación por elemento específico
const elementoTipoStore = useElementoTipoStore();
const { firstTipoProducto } = elementoTipoStore;
const { productTypeList } = storeToRefs(elementoTipoStore);
const { firstElementoGrupo } = useElementoGrupoStore();
// const productTypeList = ref([])
const elementoSeleccionado = ref(null);
const configuracionCompleta = ref(null);

// Computed para el título del modal
const tituloModal = computed(() => {
  switch (componente.value) {
    case 'SettingCreateProduct':
      return 'Nuevo producto personalizado';
    case 'SettingEditProduct':
      return elementoSeleccionado.value?.Codigo
        ? 'Editar producto personalizado'
        : 'Duplicar producto personalizado';
    case 'SettingDetailProduct':
      return 'Detalle del producto personalizado';
    default:
      return 'Producto personalizado';
  }
});

const abrirModalNuevoProducto = () => {
  modalNuevoProductoVisible.value = true;
};

const cerrarModalNuevoProducto = () => {
  elementoSeleccionado.value = null;
  configuracionCompleta.value = null;
  modalNuevoProductoVisible.value = false;
};

// Funciones para controlar el estado de loading
const setLoadingEdit = (codigo, loading) => {
  if (loading) {
    loadingEdit.value.add(codigo);
  } else {
    loadingEdit.value.delete(codigo);
  }
};

const setLoadingDuplicate = (codigo, loading) => {
  if (loading) {
    loadingDuplicate.value.add(codigo);
  } else {
    loadingDuplicate.value.delete(codigo);
  }
};

const isLoadingEdit = (codigo) => loadingEdit.value.has(codigo);
const isLoadingDuplicate = (codigo) => loadingDuplicate.value.has(codigo);

const verElementoTipo = async (elemento) => {
  if (elemento) {
    elementoSeleccionado.value = elemento;
    // elementoTipoProcesosStore.select(elemento.Codigo)
    componente.value = 'SettingDetailProduct';
    abrirModalNuevoProducto();
  }
};

// Función para cargar la configuración completa desde el backend
const cargarConfiguracionCompleta = async (codigoElementoTipo) => {
  if (!codigoElementoTipo) return null;

  try {
    const response = await elementoTipoService.getConfiguracion(codigoElementoTipo);

    if (response && response.success && response.data) {
      const datos = response.data;

      // Función para generar IDs únicos
      const uid = () => Math.random().toString(36).slice(2, 9);

      // Preparar estructura completa
      const configuracion = {
        Codigo: codigoElementoTipo || null,
        Descripcion: datos.Descripcion,
        ElementoBase: datos.ElementoBase,
        Activo: datos.Activo,
        Tintas: datos.Tintas,
        procesosConCategorias: [],
        procesosConfiguracion: [],
        unidadesMedida: Array.isArray(datos.unidadesMedida)
          ? datos.unidadesMedida.map((unidad) => unidad.Codigo)
          : [],
      };

      // Procesar categorías de materiales
      const procesosMap = new Map();

      if (datos.categoriasMaterial && Array.isArray(datos.categoriasMaterial)) {
        datos.categoriasMaterial.forEach((categoria) => {
          if (!procesosMap.has(categoria.Proceso.Codigo)) {
            procesosMap.set(categoria.Proceso.Codigo, {
              _uid: uid(),
              CodigoProceso: categoria.Proceso.Codigo,
              Proceso: categoria.Proceso,
              categorias: [],
            });
          }

          const proceso = procesosMap.get(categoria.Proceso.Codigo);
          proceso.categorias.push({
            _uid: uid(),
            CategoriaMaterial: categoria.CategoriaMaterial || null,
            MultiMaterial: categoria.MultiMaterial || false,
            SumaGramaje: categoria.SumaGramaje || false,
            DimensionesAutomaticas: categoria.DimensionesAutomaticas || false,
            ProcesoPrecedente: categoria.ProcesoPrecedente || false,
            Secciones: [],
          });
        });
      }

      configuracion.procesosConCategorias = Array.from(procesosMap.values());

      // Procesar configuración de procesos
      if (datos.procesos && Array.isArray(datos.procesos)) {
        configuracion.procesosConfiguracion = datos.procesos.map((proceso) => {
          const nuevaConfiguracion = {
            _uid: uid(),
            CodigoProceso: proceso.Proceso.Codigo,
            Proceso: proceso.Proceso || null,
            CodigoSeccion: proceso.Seccion?.Codigo || null,
            Seccion: proceso.Seccion || null,
            PorDefecto: proceso.PorDefecto || false,
            AdmiteSuaje: proceso.AdmiteSuaje || false,
            CodigoRecurso: proceso.Recurso?.Codigo || null,
            Recurso: proceso.Recurso || null,
            codigosMateriales: proceso.codigosMateriales || [],
            materialesPorCategoria: {},
          };

          // Convertir materiales a la nueva estructura agrupada por categoría
          if (proceso.materiales && Array.isArray(proceso.materiales)) {
            proceso.materiales.forEach((material) => {
              const codigoCategoria = material.MaterialTipo?.CodigoMaterialCategoria;

              if (codigoCategoria) {
                if (!nuevaConfiguracion.materialesPorCategoria[codigoCategoria]) {
                  nuevaConfiguracion.materialesPorCategoria[codigoCategoria] = [];
                }

                nuevaConfiguracion.materialesPorCategoria[codigoCategoria].push({
                  materialId: material.Codigo,
                  _uid: uid(),
                });
              }
            });
          }

          return nuevaConfiguracion;
        });
      }

      return configuracion;
    } else {
      console.log('No se encontró configuración para este elemento tipo');
      return null;
    }
  } catch (error) {
    console.error('Error al cargar configuración:', error);
    ElMessage.warning(
      'No se pudo cargar la configuración del producto o no existe configuración previa',
    );
    return null;
  }
};

const modificarElementoTipo = async (elemento) => {
  console.log('Modificar elemento tipo:', elemento);
  if (elemento) {
    try {
      setLoadingEdit(elemento.Codigo, true);

      // Cargar la configuración completa antes de abrir el modal
      configuracionCompleta.value = await cargarConfiguracionCompleta(elemento.Codigo);

      elementoSeleccionado.value = elemento;
      componente.value = 'SettingEditProduct';
      abrirModalNuevoProducto();
    } catch (error) {
      console.error('Error al cargar configuración para editar:', error);
      ElMessage.error('Error al cargar la configuración del producto');
    } finally {
      setLoadingEdit(elemento.Codigo, false);
    }
  }
};

const editarDesdeDetalle = () => {
  componente.value = 'SettingEditProduct';
};

const duplicarElementoTipo = async (elemento) => {
  if (elemento) {
    try {
      setLoadingDuplicate(elemento.Codigo, true);

      // Cargar la configuración completa del elemento original
      const configuracionOriginal = await cargarConfiguracionCompleta(elemento.Codigo);

      if (configuracionOriginal) {
        // Limpiar los campos que no deben copiarse en la duplicación
        configuracionCompleta.value = {
          ...configuracionOriginal,
          Codigo: null, // Sin código para que sea tratado como nuevo
          Descripcion: '', // Sin descripción para que el usuario la ingrese
          ElementoBase: null, // Sin elemento base seleccionado para que el usuario lo seleccione
        };
      }

      // Preparar datos del elemento seleccionado para el modal
      elementoSeleccionado.value = {
        ...elemento,
        Codigo: null,
        Descripcion: `${elemento.Descripcion} (Copia)`,
        ElementoBase: null,
      };

      componente.value = 'SettingEditProduct';
      abrirModalNuevoProducto();

      ElMessage.info(
        'Modo duplicación activado. Ingrese la descripción y seleccione el elemento base, luego modifique los procesos según necesite.',
      );
    } catch (error) {
      console.error('Error al cargar configuración para duplicar:', error);
      ElMessage.error('Error al cargar la configuración del producto para duplicar');
    } finally {
      setLoadingDuplicate(elemento.Codigo, false);
    }
  }
};

const nuevoElementoTipo = async () => {
  componente.value = 'SettingCreateProduct';
  abrirModalNuevoProducto();
};

const fetchProductoTipoList = () => {
  isLoading.value = true;
  try {
    firstTipoProducto(0);
    // elementoTipoService.findAll({
    //   page: 0
    // }).then((response) => {
    //   productTypeList.value = response || []
    //   isLoading.value = false
    // }).catch((error) => {
    //   console.error('Error al cargar productos personalizados:', error)
    //   ElMessage.error('Error al cargar productos personalizados')
    //   isLoading.value = false
    // })
  } catch (error) {
    console.error('Error al cargar productos personalizados:', error);
    ElMessage.error('Error al cargar productos personalizados');
  }
};

const onProductoCreado = async (created) => {
  console.log('Producto creado:', created);
  // Recargar la lista después de crear un producto
  fetchProductoTipoList();
  if (created?.crearGrupo) {
    firstElementoGrupo(0);
  }
};

const handleGuardarProducto = async (data) => {
  fetchProductoTipoList();
  // Cerrar el modal
  cerrarModalNuevoProducto();
};
onBeforeMount(async () => {
  fetchProductoTipoList();
});
</script>
